import React from 'react';
import { Badge } from '@components/atoms/Badge';
import ReactSelect from '@components/atoms/ReactSelect';
import { Loading } from '@components/atoms/Loading';
import { UserOrganizationRoleStatus } from '@models/user.model';
import { getTranslatedRoleLabel } from '@constants/roleHierarchy';
import { useTranslation } from '@hooks/useTranslation';

export const MEMBERSHIP_STATUS_BADGE: Record<
  UserOrganizationRoleStatus,
  { variant: 'success' | 'error' | 'warning' | 'secondary'; label: string }
> = {
  [UserOrganizationRoleStatus.ACTIVE]: { variant: 'success', label: 'Active' },
  [UserOrganizationRoleStatus.DISABLED]: { variant: 'error', label: 'Disabled' },
  [UserOrganizationRoleStatus.READ_ONLY]: {
    variant: 'warning',
    label: 'Read Only',
  },
  [UserOrganizationRoleStatus.OVERDUE]: {
    variant: 'secondary',
    label: 'Overdue',
  },
};

interface RoleOption {
  value: string;
  label: string;
}

interface UserOrganizationRoleRowProps {
  organizationName: string;
  role: string;
  status?: UserOrganizationRoleStatus;
  /** Roles the signed-in user may assign here. Omit to render read-only. */
  roleOptions?: RoleOption[];
  onRoleChange?: (role: string) => void;
  isSaving?: boolean;
  /** Marks the organization the signed-in user is currently acting in. */
  isCurrent?: boolean;
}

/**
 * One organization a user belongs to, with the role they hold there.
 *
 * The role lives in user_organization_roles — a different row per organization —
 * so changing it here targets that organization, NOT the one the signed-in user
 * is currently acting in.
 */
export const UserOrganizationRoleRow: React.FC<
  UserOrganizationRoleRowProps
> = ({
  organizationName,
  role,
  status,
  roleOptions,
  onRoleChange,
  isSaving = false,
  isCurrent = false,
}) => {
  const tEnumLabels = useTranslation('enumLabels');
  const tCommon = useTranslation('common');
  // A role the caller cannot assign (OWNER, SUPER_ADMIN, or one above their own
  // level) has no option to select, so the dropdown would render blank and
  // submitting it would demote the user. Show the label instead.
  const isEditable =
    !!onRoleChange && !!roleOptions?.some((option) => option.value === role);

  const statusBadge = status ? MEMBERSHIP_STATUS_BADGE[status] : undefined;

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2">
      {/* Keeps the organization name readable instead of letting the role
          dropdown squeeze it down to an ellipsis. */}
      <div className="flex min-w-[110px] flex-1 items-center gap-2">
        <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
          {organizationName}
        </span>
        {isCurrent && (
          <Badge size="sm" variant="primary">
            Current
          </Badge>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {isEditable ? (
          <div className="w-[170px]">
            <ReactSelect
              options={roleOptions ?? []}
              value={roleOptions?.find((option) => option.value === role)}
              onChange={(selected) => {
                const value = Array.isArray(selected)
                  ? null
                  : (selected as RoleOption | null)?.value;
                if (value && value !== role) onRoleChange?.(value);
              }}
              isDisabled={isSaving}
              isSearchable={false}
            />
          </div>
        ) : (
          <Badge size="sm" variant="secondary">
            {getTranslatedRoleLabel(role, tEnumLabels, tCommon.unknown)}
          </Badge>
        )}

        {isSaving ? (
          <Loading size="sm" />
        ) : (
          statusBadge && (
            <Badge size="sm" variant={statusBadge.variant}>
              {statusBadge.label}
            </Badge>
          )
        )}
      </div>
    </div>
  );
};
